// Editorial dates stay fixed across builds and change only when the specs,
// prices and owner sources are re-checked. Copy lives in the repo; the photos
// are hosted on Contentful.
const biggestPrintersGuide = {
  path: '/guides/biggest-3d-printers/',
  title: 'What is the biggest 3D printer you can buy in 2026?',
  cardTitle: 'The biggest 3D printers in 2026',
  metaTitle: 'Biggest 3D Printers in 2026: Build Sizes, Prices & Options',
  description:
    'Build volumes and prices for the largest 3D printers you can buy in 2026, what owners say about running them, and how parts bigger than any bed get made.',
  publishedDate: '2026-10-27',
  reviewedDate: '2026-10-27',
  reviewedDateLabel: 'October 27, 2026',
}

const photos = {
  printers: {
    src: 'https://images.ctfassets.net/dovsnf6qrcem/16zgIkjEpkbS4fSqzGirY1/760694e1be95dd5ed538eb71f35a3f5e/L1030539.jpg',
    alt: 'Two UnionTech Lite600HD industrial resin 3D printers in the PrintAWorld studio',
    caption:
      'Two of the UnionTech Lite600HD SLA printers PrintAWorld uses for large-format work.',
    width: 1200,
    height: 800,
  },
  example: {
    src: 'https://images.ctfassets.net/dovsnf6qrcem/4hAGwqouIiAyYO2Y9zyxxP/b7d0f1fdaa72a9c63ee06a20297a234a/_DSC0368.JPG',
    alt: 'A green Deepsea Challenger submersible model on a display stand',
    caption:
      'A Deepsea Challenger model from PrintAWorld’s large-format portfolio.',
    width: 900,
    height: 1400,
  },
}

const toInches = (mm) => (mm / 25.4).toFixed(1)
const volume = (dims) => dims.join(' × ')
const volumeInches = (dims) => dims.map(toInches).join(' × ')

// Build volumes are W × D × H as each maker states them; prices are what each
// official US store showed on the check date (current price, then regular).
// affiliateUrl stays null until a program is approved; links then switch to
// rel="sponsored" and the disclosure shows automatically.
const desktopPrinters = [
  {
    name: 'Bambu Lab H2D',
    type: 'Filament, enclosed, dual nozzle',
    dims: [325, 320, 325],
    dimsNote: 'single-nozzle printing',
    price: '$1,399',
    regular: '$1,549',
    specUrl: 'https://bambulab.com/en/h2d/tech-specs',
    storeUrl: 'https://us.store.bambulab.com/collections/3d-printer',
    affiliateUrl: null,
  },
  {
    name: 'Bambu Lab A2L',
    type: 'Filament, open frame',
    dims: [330, 320, 325],
    price: '$439',
    regular: '$469',
    specUrl: 'https://bambulab.com/en/a2l/specs',
    storeUrl: 'https://us.store.bambulab.com/collections/3d-printer',
    affiliateUrl: null,
  },
  {
    name: 'Bambu Lab H2S',
    type: 'Filament, enclosed',
    dims: [340, 320, 340],
    price: '$1,049',
    regular: '$1,199',
    specUrl: 'https://bambulab.com/en/h2s',
    storeUrl: 'https://us.store.bambulab.com/collections/3d-printer',
    affiliateUrl: null,
  },
  {
    name: 'Elegoo Neptune 4 Max',
    type: 'Filament, open frame',
    dims: [420, 420, 480],
    price: '$359',
    regular: '$559.99',
    specUrl: 'https://us.elegoo.com/products/neptune-4-max-fdm-3d-printer',
    storeUrl: 'https://us.elegoo.com/products/neptune-4-max-fdm-3d-printer',
    affiliateUrl: null,
  },
  {
    name: 'Elegoo OrangeStorm Giga',
    type: 'Filament, open frame',
    dims: [800, 800, 1000],
    price: '$2,299',
    regular: '$3,125',
    specUrl: 'https://us.elegoo.com/products/orangestorm-giga',
    storeUrl: 'https://us.elegoo.com/products/orangestorm-giga',
    affiliateUrl: null,
  },
  {
    name: 'Formlabs Form 4L',
    type: 'Resin (MSLA), Basic Package',
    dims: [353, 196, 350],
    price: '$7,125',
    regular: null,
    specUrl: 'https://formlabs.com/store/3d-printers/form-4l/',
    storeUrl: 'https://formlabs.com/store/3d-printers/form-4l/',
    affiliateUrl: null,
  },
]

const industrialPrinters = [
  {
    name: 'BigRep ONE',
    type: 'Filament (FDM)',
    dims: [1005, 1005, 1005],
    specUrl: 'https://bigrep.com/bigrep-one/',
  },
  {
    name: 'Stratasys F770',
    type: 'Filament (FDM)',
    dims: [1000, 610, 610],
    specUrl:
      'https://www.stratasys.com/en/3d-printers/printer-catalog/fdm/f770-printer/',
  },
  {
    name: '3D Systems SLA 750',
    type: 'Resin (SLA)',
    dims: [750, 750, 550],
    specUrl: 'https://www.3dsystems.com/3d-printers/sla-750',
  },
]

const record = {
  holder: 'University of Maine Advanced Structures and Composites Center',
  title: 'Largest prototype polymer 3D printer',
  volume: '343.61 m³ (12,134.47 ft³)',
  date: 'October 10, 2019',
  url: 'https://www.guinnessworldrecords.com/world-records/432577-largest-prototype-polymer-3d-printer',
}

// Published on /large-scale-3d-printing-service/. The Lite600HD's height is
// not stated here until the studio confirms it (UnionTech lists 400 mm for its
// current Lite 600 models; the service page says 19.7 in).
const studio = {
  footprint: '600 × 600 mm (23.6 × 23.6 in)',
  finishedSize: 'six feet or more',
  turnaround: '1–2 weeks',
  turnaroundLarge: '1–2 months',
}

const ownerSources = {
  giga: {
    title:
      'The 3D Printer That Broke Me: My 10-Month Battle with the Elegoo OrangeStorm Giga',
    channel: 'Major Hardware',
    date: 'January 2025',
    url: 'https://www.youtube.com/watch?v=xAPw2INpqJw',
  },
  neptune: {
    title:
      'Neptune 4 Max - You MUST do these steps to get good prints and fix the problems',
    channel: 'Dave Tries This',
    date: 'May 2024',
    url: 'https://www.youtube.com/watch?v=C5ZZnfse4LI',
  },
  carlex: {
    title: 'Testing the Creality K2 Plus Electrical Power Usage in Watts',
    channel: 'Carlex DIY',
    date: 'December 2024',
    url: 'https://www.youtube.com/watch?v=ep8C0OfCOq0',
  },
  helmet: {
    title: 'Mandalorian Helmets Using Small 3d Printers',
    channel: 'I Like To Make Stuff',
    date: 'January 2021',
    url: 'https://www.youtube.com/watch?v=z7rOsba3WbY',
  },
}

const at = (key, seconds) => `${ownerSources[key].url}&t=${seconds}s`

const cutTool = 'https://help.prusa3d.com/article/cut-tool_1779'

const faqs = [
  {
    question: 'What is the biggest 3D printer you can buy for home use?',
    answer:
      'Among printers sold directly on official US stores in late October 2026, the Elegoo OrangeStorm Giga has the largest build volume, at 800 × 800 × 1000 mm (31.5 × 31.5 × 39.4 in), for $2,299 on sale ($3,125 regular). One owner reports it weighs about 145 kg (320 lb) and needed its own 20-amp circuit.',
  },
  {
    question: 'How big can a 3D print be?',
    answer:
      'One print is limited to the printer’s build volume: roughly 32–48 cm on a side for large desktop printers, and about 1 m for industrial machines such as the BigRep ONE or Stratasys F770. Larger objects are printed in sections and joined, so the finished size is limited by the design, handling and finishing rather than the printer.',
  },
  {
    question: 'How do you 3D print something bigger than the print bed?',
    answer:
      'Cut the model into sections that fit, add alignment features such as dowels or connectors, print the pieces, then glue, fill and sand the seams. Slicers such as PrusaSlicer include a cut tool that can add connectors and dowel pins.',
  },
  {
    question: 'How much does a large 3D printer cost?',
    answer:
      'Large desktop printers on official US stores cost about $359–$1,549 in late October 2026, and the 800 mm Elegoo OrangeStorm Giga costs $2,299–$3,125. Formlabs’ large Form 4L resin printer starts at $7,125. Industrial large-format machines from BigRep, Stratasys and 3D Systems are sold by quote.',
  },
  {
    question:
      'Is it cheaper to buy a large 3D printer or outsource big prints?',
    answer:
      'For a one-off sculpture, prop or display, outsourcing usually costs less than buying a printer large enough, plus the space, power, materials and failed multi-day prints. Buying makes more sense if you will print large parts often and have room for the machine.',
  },
]

module.exports = {
  biggestPrintersGuide,
  photos,
  desktopPrinters,
  industrialPrinters,
  record,
  studio,
  ownerSources,
  at,
  cutTool,
  faqs,
  volume,
  volumeInches,
}
